import React, { lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Backdrop from '@mui/material/Backdrop';
import { canRedirectTo } from '@core/constants/appVersion';
import { allowAutomaticAppSwitch } from '@core/utils/appSwitch';
import buildAppSwitchUrl from '@core/utils/buildAppSwitchUrl';
import { persistSplashOrganization, setStartLoaderOrganization } from '@core/utils/startLoader';
import useSharedStudyAppVersion from '../hooks/useSharedStudyAppVersion';

const ReadOnlyViewer = lazy(() => import('./ReadOnlyViewer'));

// Shared-study links (/study/view?s=<hash>) are served on whichever app
// version the study's organization chose in Personalización (default v2),
// regardless of any Radiologist app-version preference.
const ShareLinkRedirect = () => {
  const [searchParams] = useSearchParams();
  const { isResolved, targetVersion, organizationId } = useSharedStudyAppVersion(
    searchParams.get('s') ?? '',
  );
  const { accounts } = useMsal();
  const hasSignedInUser = accounts.length > 0;

  // index.html already shows the logo of the link's `o` param; links without it
  // (older ones, WhatsApp) or with a wrong one get corrected once the study's
  // org is known. Anonymous patients also keep it for later full page loads
  // (birthdate submit, /redirect after sign-up); signed-in users keep their own.
  useEffect(() => {
    if (!organizationId) return;

    setStartLoaderOrganization(organizationId);
    if (!hasSignedInUser) persistSplashOrganization(organizationId);
  }, [organizationId, hasSignedInUser]);
  // When the target URL isn't configured (missing/unresolved pipeline
  // variable) redirecting would just replace this page's URL with itself
  // (buildAppSwitchUrl falls back to a same-origin relative path), looping
  // forever — serve the viewer locally instead. allowAutomaticAppSwitch also
  // stops a v2 <-> v3 ping-pong when both apps resolve different versions
  // (e.g. one of them failing the shared-study lookup).
  const shouldRedirect =
    isResolved && allowAutomaticAppSwitch(targetVersion) && canRedirectTo(targetVersion);

  useEffect(() => {
    if (shouldRedirect) {
      window.location.replace(buildAppSwitchUrl(targetVersion));
    }
  }, [shouldRedirect, targetVersion]);

  if (!isResolved || shouldRedirect) {
    return <Backdrop open>Cargando</Backdrop>;
  }

  return (
    <Suspense fallback={<Backdrop open>Cargando</Backdrop>}>
      <ReadOnlyViewer />
    </Suspense>
  );
};

export default ShareLinkRedirect;
